import React, { useState } from "react";
import { Headphones, Send, Heart } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

/**
 * StationRequestForm — listener-facing "Request a song" form.
 *
 * Renders only when a host session is live. Posts to
 * /api/radio/host/{slug}/requests; backend enforces a 60s per-user
 * cooldown so this UI doesn't need its own throttle.
 */
export default function StationRequestForm({ slug }) {
  const { user } = useAuth();
  const [trackTitle, setTrackTitle] = useState("");
  const [artist, setArtist] = useState("");
  const [message, setMessage] = useState("");
  const [dedicateTo, setDedicateTo] = useState("");
  const [busy, setBusy] = useState(false);
  const [sentAt, setSentAt] = useState(0);

  const submit = async (e) => {
    e?.preventDefault();
    if (!user) { toast.info("Sign in to request a song"); return; }
    const t = trackTitle.trim();
    if (!t) { toast.info("What song should the DJ play?"); return; }
    setBusy(true);
    try {
      await api.post(`/radio/host/${slug}/requests`, {
        track_title: t,
        artist: artist.trim(),
        message: message.trim(),
        dedicate_to: dedicateTo.trim(),
      });
      setTrackTitle(""); setArtist(""); setMessage(""); setDedicateTo("");
      setSentAt(Date.now());
      toast.success("Request sent — the DJ will see it!");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  if (!slug) return null;
  return (
    <section data-testid="radio-station-request-form" className="rounded-xl border border-white/10 bg-black/40 mt-6 p-4">
      <header className="flex items-center gap-2 mb-3">
        <Headphones className="w-4 h-4 text-rose-300" />
        <h3 className="text-[11px] uppercase tracking-[0.25em] font-black text-white/85">Request a song</h3>
      </header>
      <form onSubmit={submit} className="space-y-2">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <input
            data-testid="request-track-input"
            value={trackTitle}
            onChange={(e) => setTrackTitle(e.target.value.slice(0, 160))}
            placeholder="Song title *"
            disabled={!user || busy}
            className="bg-black/40 border border-white/10 focus:border-rose-400/60 outline-none rounded px-3 py-2 text-[12px] text-white placeholder-white/30"
          />
          <input
            data-testid="request-artist-input"
            value={artist}
            onChange={(e) => setArtist(e.target.value.slice(0, 120))}
            placeholder="Artist (optional)"
            disabled={!user || busy}
            className="bg-black/40 border border-white/10 focus:border-rose-400/60 outline-none rounded px-3 py-2 text-[12px] text-white placeholder-white/30"
          />
        </div>
        <input
          data-testid="request-dedicate-input"
          value={dedicateTo}
          onChange={(e) => setDedicateTo(e.target.value.slice(0, 80))}
          placeholder="Dedicate to (optional)"
          disabled={!user || busy}
          className="w-full bg-black/40 border border-white/10 focus:border-rose-400/60 outline-none rounded px-3 py-2 text-[12px] text-white placeholder-white/30"
        />
        <textarea
          data-testid="request-message-input"
          value={message}
          onChange={(e) => setMessage(e.target.value.slice(0, 240))}
          placeholder="Shout-out to the DJ (optional)"
          disabled={!user || busy}
          rows={2}
          className="w-full bg-black/40 border border-white/10 focus:border-rose-400/60 outline-none rounded px-3 py-2 text-[12px] text-white placeholder-white/30"
        />
        <div className="flex items-center justify-between">
          <span className="text-[10px] text-white/40">
            <Heart className="w-3 h-3 inline-block text-rose-300" /> 1 request / minute / station
          </span>
          <button
            data-testid="request-submit"
            type="submit"
            disabled={!user || busy || !trackTitle.trim()}
            className="bg-rose-500 hover:bg-rose-400 disabled:opacity-40 text-black font-black px-4 py-2 rounded inline-flex items-center gap-1 text-[12px] uppercase tracking-wider"
          >
            <Send className="w-3.5 h-3.5" /> {busy ? "Sending…" : sentAt ? "Sent — send another" : "Send"}
          </button>
        </div>
      </form>
    </section>
  );
}
